import { Link } from 'react-router-dom';
import { fmtDateTime } from './FormBits.jsx';
import { clientPath } from '../lib/clientLink.js';

// Session 6 Part B: the persistent onboarding checklist. Every state is
// derived from v_portal_onboarding (registry data), never from session
// memory, so it reads the same after leaving and coming back.

export function checklistItems(row) {
  if (!row) return [];
  const own = row.client_id === 'digitallotus';   // the agency's own tenant: no invite, no client login
  const agencyApproved = row.plan_agency_approved_at || (own ? row.plan_client_approved_at : null);
  const posts = row.posts_total || 0;
  return [
    { key: 'created',   label: 'Client created',                 at: row.created_at },
    { key: 'invite',    label: 'Invite sent',                    at: row.invite_sent_at,    na: own && !row.invite_sent_at ? 'Not needed for the agency\'s own tenant' : null },
    { key: 'password',  label: 'Password set (first sign-in)',   at: row.password_set_at,   na: own && !row.password_set_at ? 'Not needed for the agency\'s own tenant' : null },
    { key: 'baseline',  label: 'Baseline analysis run',          at: row.baseline_run_at,   na: own && !row.baseline_run_at ? 'Digital Lotus runs on its own baseline file' : null },
    { key: 'strategy',  label: 'Strategy approved',              at: row.strategy_approved_at, na: own && !row.strategy_approved_at ? 'Digital Lotus runs on its own baseline file' : null },
    { key: 'plan',      label: 'First quarterly plan generated', at: row.plan_generated_at },
    { key: 'agency_ok', label: 'Agency approved plan',           at: agencyApproved },
    { key: 'client_ok', label: 'Client approved plan',           at: row.plan_client_approved_at, detail: own ? 'Single approval for Digital Lotus' : null },
    { key: 'scheduled', label: 'Posts scheduled',                at: row.posts_scheduled_at, detail: posts ? `${posts} posts have their questions scheduled` : null },
  ].map((it) => ({ ...it, state: it.at ? 'done' : it.na ? 'na' : 'todo' }));
}

export function checklistComplete(row) {
  const items = checklistItems(row);
  return items.length > 0 && items.every((i) => i.state !== 'todo');
}

export default function OnboardingChecklist({ row, compact = false }) {
  const items = checklistItems(row);
  if (!items.length) return null;
  const done = items.filter((i) => i.state !== 'todo').length;
  if (compact) {
    return (
      <section className="progress-card" aria-label="Onboarding progress">
        <div className="progress-head">
          <div>
            <div className="progress-title">Onboarding: {done} of {items.length} steps done</div>
            <div className="progress-sub">
              Next: {items.find((i) => i.state === 'todo')?.label || 'all done'}
            </div>
          </div>
          <Link className="link-btn" to={clientPath(`/admin/clients/${row.client_id}/settings?tab=onboarding`, row.client_id)}>Open checklist</Link>
        </div>
        <div className="progress-bar" role="progressbar" aria-valuenow={done} aria-valuemin={0} aria-valuemax={items.length}>
          <span style={{ width: `${Math.round((done / items.length) * 100)}%` }} />
        </div>
      </section>
    );
  }
  return (
    <ol className="checklist">
      {items.map((it) => (
        <li key={it.key} className={`check-item check-${it.state}`}>
          <span className="check-mark" aria-hidden="true">{it.state === 'done' ? '✓' : it.state === 'na' ? '–' : ''}</span>
          <div className="check-body">
            <div className="check-label">{it.label}</div>
            <div className="check-when">
              {it.state === 'done' && fmtDateTime(it.at)}
              {it.state === 'na' && it.na}
              {it.state === 'todo' && 'Not yet'}
              {it.detail && <> · {it.detail}</>}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}
